import { OPTION_TYPE } from '../constant/product-option';
import { CreateOrderSheetOrderItemInput, ProductAdditionalOptionInput } from '../graphql/select-option_types.2';
import type { CreateOrderItemParams, CustomInputItemType, ItemCardType } from '../type/common.type';
import { checkZigzinOrVendorFlexDeliveryType } from '../util/checkers/zigzin-checker';
import { applyBogoPromotion, distributeBogoPrice } from '../util/promotions/bogo-promotion.util';
import { applyFirstOrderDiscount } from '../util/promotions/first-order-discount.util';
import { applyFunnelInfo } from '../util/promotions/funnel-promotion.util';

/**
 * 주문 아이템 목록을 생성합니다
 * @param params - 주문 아이템 생성 파라미터
 * @returns 생성된 주문 아이템 배열
 */
export const createOrderItemList = (params: CreateOrderItemParams): CreateOrderSheetOrderItemInput[] => {
  const { items } = params;
  const bogo_count = (params.bogo_info?.required_quantity ?? 1) + 1;
  const flat_order_items = items.flatMap((item) => flatItemCardList(item));
  const total_price = flat_order_items.reduce((total, item) => total + item.price, 0) / bogo_count;
  const distribute_bogo_price_list = distributeBogoPrice(total_price, flat_order_items.length);

  const order_item_list = flat_order_items.map((item, index) => {
    const is_zigzin_option = checkZigzinOrVendorFlexDeliveryType(item.delivery_type ?? null);
    const is_additional_option = item.option_type === OPTION_TYPE.ADDITIONAL;

    const additional_option_detail_list = covertCustomInputStatesToCustomInputItemType(item.custom_input_list ?? []);

    let order_item = createOrderItem(item, additional_option_detail_list);

    // 프로모션 정보 추가
    order_item = applyFunnelInfo(order_item, params.funnelType ?? '');
    order_item = applyFirstOrderDiscount(order_item, params.first_order_discount, {
      is_zigzin_option,
      is_zigzin_matching_product: params.is_zigzin_matching_product,
    });
    order_item = applyBogoPromotion(order_item, distribute_bogo_price_list[index], params, {
      is_additional_option,
    });

    return order_item;
  });

  return order_item_list;
};

/**
 * 아이템 카드 내 일반 아이템과 bogo 아이템을 flatMap 처리합니다
 * @param item - 상품 아이템 카드
 * @returns 아이템 카드 리스트
 */
const flatItemCardList = (item: ItemCardType) => {
  const base_order_item: ItemCardType = {
    ...item,
  };

  const bogo_order_items: ItemCardType[] = item.bogo_item_list.map((bogo_item) => ({
    ...bogo_item,
    count: item.count,
    bogo_item_list: [],
    display_name: '',
    minimum_order_quantity: 0,
  }));

  return [base_order_item, ...bogo_order_items];
};

/**
 * 기본 주문 아이템 생성
 * @param item - 상품 아이템
 * @param additional_option_detail_list - 추가 옵션 정보
 * @returns 주문 아이템
 */
const createOrderItem = (
  item: ItemCardType,
  additional_option_detail_list: ProductAdditionalOptionInput[] = [],
): CreateOrderSheetOrderItemInput => {
  return {
    product_item_id: item.id,
    price: item.price,
    quantity: item.count,
    additional_option_detail_list,
  };
};

/**
 * 아이템 카드 내 커스텀 옵션을 API input 값에 맞춘 리스트로 변환합니다
 * @param custom_input_list - 커스텀 텍스트 옵션 배열
 * @returns 추가 옵션 상세 리스트
 */
export const covertCustomInputStatesToCustomInputItemType = (
  custom_input_list: CustomInputItemType[] = [],
): ProductAdditionalOptionInput[] => {
  try {
    return custom_input_list.map((obj) => {
      const [key, value] = Object.entries(obj)[0];
      return { name: key, value };
    });
  } catch (error) {
    console.error('입력형 옵션 상세 리스트 생성 중 오류 발생:', error);
    return [];
  }
};
